'use client';

import Image from 'next/image';
import Link from 'next/link';
import {
  BREED_HANDBOOK_FIT_CHECK_CTA,
  breedHandbookFitCheckPath,
  breedHandbookPersonalizedFor,
  breedHandbookTitle,
  myBreedHandbookPath
} from '@paction-2.0/util/constants/breedHandbooks';

import { useAuth } from '@/lib/auth/AuthProvider';

import styles from './breedHandbookAd.module.scss';
import { trackHandbookEvent } from './handbookEvents';
import { handbookSerif } from './handbookFonts';
import { useBreedHandbooksEnabled } from './useBreedHandbooksEnabled';
import { useHandbookEventsViewer } from './useHandbookEventsViewer';
import { useOwnsHandbook } from './useOwnsHandbook';
import { useTrackWhenVisible } from './useTrackWhenVisible';

type BreedHandbookAdProps = {
  breedName: string;
  breedSlug: string;
  photoUrl: string | null;
  placement: 'breed_page' | 'search_page';
  className?: string;
};

export function BreedHandbookAd({
  breedName,
  breedSlug,
  photoUrl,
  placement,
  className
}: BreedHandbookAdProps) {
  useHandbookEventsViewer();
  const { isEnabled } = useBreedHandbooksEnabled();
  const { session } = useAuth();
  const ownsHandbook = useOwnsHandbook(breedSlug);
  const cta = ownsHandbook ? 'open' : 'preview';
  const adRef = useTrackWhenVisible<HTMLElement>(
    isEnabled ? `${placement}:${breedSlug}` : null,
    () =>
      trackHandbookEvent('handbook_ad_view', {
        surface: placement,
        breed_slug: breedSlug,
        owned: ownsHandbook
      })
  );
  if (!isEnabled) return null;

  const firstName = session?.profile?.account?.firstName ?? null;

  return (
    <div className={className}>
      <aside ref={adRef} className={`${styles.ad} ${handbookSerif.variable}`}>
        {photoUrl && (
          <div className={styles.photo}>
            <div className={styles.photoFrame}>
              <Image
                src={photoUrl}
                alt=""
                fill
                sizes="(max-width: 1024px) 18rem, 240px"
                className={styles.photoImage}
              />
            </div>
          </div>
        )}
        <div className={styles.text}>
          <p className={styles.eyebrow}>
            {ownsHandbook
              ? 'Your guide'
              : breedHandbookPersonalizedFor(firstName)}
          </p>
          <p className={styles.title}>{breedHandbookTitle(breedName)}</p>
          <span className={styles.rule} aria-hidden="true" />
          <p className={styles.body}>
            {ownsHandbook
              ? `Your screening guide, breed match and question script for ${breedName} breeders are ready whenever you need them.`
              : `See how the ${breedName} fits your home with a free fit check. The full guide goes further: every health screening and DNA test explained, and a question script written for your household.`}
          </p>
          <Link
            href={
              ownsHandbook
                ? myBreedHandbookPath(breedSlug)
                : breedHandbookFitCheckPath(breedSlug)
            }
            className={styles.cta}
            onClick={() =>
              trackHandbookEvent('handbook_ad_click', {
                surface: placement,
                breed_slug: breedSlug,
                owned: ownsHandbook,
                cta
              })
            }
          >
            {ownsHandbook ? 'Open my guide' : BREED_HANDBOOK_FIT_CHECK_CTA}
          </Link>
        </div>
      </aside>
    </div>
  );
}
